import { formatTimestamp } from '../chunk-SOHXZKYG.js';
import { asString, coerceNumber, formatShare, formatUsd } from '../chunk-JYNVEGT6.js';
import { chainDisplayName } from '../chunk-R5ZQC5YM.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/reporting/types.ts
var ReportSectionKind = {
  KPI: "kpi",
  CHART: "chart",
  TABLE: "table",
  NARRATIVE: "narrative",
  DIVIDER: "divider",
  /** Money in → subject → money out (`./flowLayout`). */
  FLOW: "flow",
  /** Ordered steps — a case's story. */
  TIMELINE: "timeline",
  /** Label / value pairs — method, provenance, limits. */
  FACTS: "facts"
};
var AUTHORABLE_SECTION_KINDS = [
  ReportSectionKind.KPI,
  ReportSectionKind.CHART,
  ReportSectionKind.TABLE,
  ReportSectionKind.NARRATIVE,
  ReportSectionKind.DIVIDER
];
var ChartKind = {
  AREA: "area",
  LINE: "line",
  BAR: "bar",
  DONUT: "donut",
  BAR_LIST: "bar_list"
};
var ReportFormat = {
  PDF: "pdf",
  CSV: "csv",
  EXCEL: "excel"
};

// src/reporting/recurrence.ts
var ScheduleFrequency = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly"
};
var WEEKDAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
function parseTimeOfDay(time) {
  const [h, m] = (time ?? "").split(":");
  const hours = Number(h);
  const minutes = Number(m);
  return {
    hours: Number.isFinite(hours) ? Math.min(23, Math.max(0, hours)) : 8,
    minutes: Number.isFinite(minutes) ? Math.min(59, Math.max(0, minutes)) : 0
  };
}
function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}
function atTimeOfDay(base, hours, minutes) {
  const d = new Date(base);
  d.setHours(hours, minutes, 0, 0);
  return d;
}
function nextRunAt(schedule, from = /* @__PURE__ */ new Date()) {
  const { hours, minutes } = parseTimeOfDay(schedule.timeOfDay);
  if (schedule.frequency === ScheduleFrequency.WEEKLY) {
    const target = schedule.weekday ?? 1;
    const d2 = atTimeOfDay(from, hours, minutes);
    let add = (target - d2.getDay() + 7) % 7;
    if (add === 0 && d2.getTime() <= from.getTime()) add = 7;
    d2.setDate(d2.getDate() + add);
    return d2.getTime();
  }
  if (schedule.frequency === ScheduleFrequency.MONTHLY) {
    const desired = schedule.dayOfMonth ?? 1;
    let year = from.getFullYear();
    let month = from.getMonth();
    for (let i = 0; i < 24; i += 1) {
      const day = Math.min(desired, daysInMonth(year, month));
      const d2 = atTimeOfDay(new Date(year, month, day), hours, minutes);
      if (d2.getTime() > from.getTime()) return d2.getTime();
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }
    return atTimeOfDay(from, hours, minutes).getTime();
  }
  const d = atTimeOfDay(from, hours, minutes);
  if (d.getTime() <= from.getTime()) d.setDate(d.getDate() + 1);
  return d.getTime();
}
function describeRecurrence(schedule) {
  const time = schedule.timeOfDay || "08:00";
  switch (schedule.frequency) {
    case ScheduleFrequency.WEEKLY:
      return `Weekly on ${WEEKDAY_LABELS[schedule.weekday ?? 1]} at ${time}`;
    case ScheduleFrequency.MONTHLY: {
      const day = schedule.dayOfMonth ?? 1;
      return `Monthly on day ${day} at ${time}`;
    }
    default:
      return `Daily at ${time}`;
  }
}
function defaultSchedule() {
  return { frequency: ScheduleFrequency.DAILY, timeOfDay: "08:00", enabled: true };
}

// src/reporting/flowLayout.ts
var DEFAULT_FLOW_WIDTH = 1e3;
var NODE_WIDTH = 220;
var CENTER_WIDTH = 190;
var NODE_MIN_HEIGHT = 46;
var NODE_GAP = 10;
var PADDING_Y = 6;
var MIN_BAND = 2;
var BAND_BUDGET = 280;
var NODE_BAND_PADDING = 14;
var MONO_ADVANCE = 0.6;
function fitLabel(text, widthPx, fontPx) {
  const max = Math.floor(widthPx / (fontPx * MONO_ADVANCE));
  if (max <= 0) return "";
  if (text.length <= max) return text;
  return max <= 1 ? "\u2026" : `${text.slice(0, max - 1)}\u2026`;
}
function sum(values) {
  return values.reduce((total, value) => total + value, 0);
}
function weight(value) {
  return Number.isFinite(value) && value > 0 ? value : 0;
}
function ribbonPath(x0, top0, x1, top1, thickness) {
  const mid = (x0 + x1) / 2;
  const bottom0 = top0 + thickness;
  const bottom1 = top1 + thickness;
  const f = (n) => Number(n.toFixed(2));
  return [
    `M${f(x0)},${f(top0)}`,
    `C${f(mid)},${f(top0)} ${f(mid)},${f(top1)} ${f(x1)},${f(top1)}`,
    `L${f(x1)},${f(bottom1)}`,
    `C${f(mid)},${f(bottom1)} ${f(mid)},${f(bottom0)} ${f(x0)},${f(bottom0)}`,
    "Z"
  ].join(" ");
}
function layoutFlow(input) {
  const width = input.width ?? DEFAULT_FLOW_WIDTH;
  const centerX = (width - CENTER_WIDTH) / 2;
  const destinationX = width - NODE_WIDTH;
  const larger = Math.max(
    sum(input.sources.map((n) => weight(n.value))),
    sum(input.destinations.map((n) => weight(n.value)))
  );
  const band = (value) => larger > 0 ? Math.max(MIN_BAND, weight(value) / larger * BAND_BUDGET) : MIN_BAND;
  const sized = (nodes2) => nodes2.map((node) => {
    const thickness = band(node.value);
    return { node, thickness, h: Math.max(NODE_MIN_HEIGHT, thickness + NODE_BAND_PADDING) };
  });
  const left = sized(input.sources);
  const right = sized(input.destinations);
  const columnHeight = (items) => items.length ? sum(items.map((i) => i.h)) + NODE_GAP * (items.length - 1) : 0;
  const leftBands = sum(left.map((i) => i.thickness));
  const rightBands = sum(right.map((i) => i.thickness));
  const centerH = Math.max(NODE_MIN_HEIGHT * 1.5, leftBands + NODE_BAND_PADDING, rightBands + NODE_BAND_PADDING);
  const inner = Math.max(columnHeight(left), columnHeight(right), centerH);
  const height = inner + PADDING_Y * 2;
  const nodes = [];
  const ribbons = [];
  const centerY = PADDING_Y + (inner - centerH) / 2;
  nodes.push({
    id: input.center.id,
    side: "center",
    x: centerX,
    y: centerY,
    w: CENTER_WIDTH,
    h: centerH,
    label: input.center.label,
    detail: input.center.detail,
    kicker: input.center.kicker,
    tone: "default",
    value: sum(input.sources.map((n) => weight(n.value)))
  });
  const place = (items, side, x, bandsTotal) => {
    let y = PADDING_Y + (inner - columnHeight(items)) / 2;
    let slot = centerY + (centerH - bandsTotal) / 2;
    for (const { node, thickness, h } of items) {
      nodes.push({
        id: node.id,
        side,
        x,
        y,
        w: NODE_WIDTH,
        h,
        label: node.label,
        detail: node.detail,
        kicker: node.kicker,
        tone: node.tone ?? "default",
        value: weight(node.value)
      });
      const nodeBandTop = y + (h - thickness) / 2;
      const d = side === "source" ? ribbonPath(x + NODE_WIDTH, nodeBandTop, centerX, slot, thickness) : ribbonPath(centerX + CENTER_WIDTH, slot, x, nodeBandTop, thickness);
      ribbons.push({ id: `${side}:${node.id}`, side, nodeId: node.id, value: weight(node.value), thickness, d });
      y += h + NODE_GAP;
      slot += thickness;
    }
  };
  place(left, "source", 0, leftBands);
  place(right, "destination", destinationX, rightBands);
  return {
    width,
    height,
    nodes,
    ribbons,
    columns: {
      source: { x: 0, w: NODE_WIDTH },
      center: { x: centerX, w: CENTER_WIDTH },
      destination: { x: destinationX, w: NODE_WIDTH }
    }
  };
}

// src/reporting/cellText.ts
function reportCellText(row, column) {
  const value = row[column.key];
  switch (column.format ?? "text") {
    case "number": {
      const n = coerceNumber(value);
      return n === void 0 ? null : n.toLocaleString("en-US");
    }
    case "usd": {
      const n = coerceNumber(value);
      return n === void 0 ? "unpriced" : formatUsd(n);
    }
    case "share": {
      const n = coerceNumber(value);
      return n === void 0 ? null : formatShare(n);
    }
    case "chain":
      return asString(value) ? chainDisplayName(String(value)) : null;
    case "datetime": {
      const n = coerceNumber(value);
      return n === void 0 ? null : `${formatTimestamp(n, { unit: "seconds", timeZone: "UTC" })} UTC`;
    }
    case "address": {
      const address = asString(value);
      if (!address) return null;
      const label = column.labelKey ? asString(row[column.labelKey]) : void 0;
      return label ? `${label} (${address})` : address;
    }
    case "hash":
    case "badge":
    case "text":
    default:
      return value === null || value === void 0 || value === "" ? null : String(value);
  }
}

// src/reporting/snapshot.ts
var REPORT_SNAPSHOT_KIND = "authe.report-snapshot";
var REPORT_SNAPSHOT_VERSION = 1;
function canonicalJson(value) {
  return JSON.stringify(sortKeys(value));
}
function sortKeys(value) {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (typeof value !== "object" || value === null) return value;
  const out = {};
  for (const key of Object.keys(value).sort()) {
    const member = value[key];
    if (member !== void 0) out[key] = sortKeys(member);
  }
  return out;
}
function reportContentHash(report) {
  const { footer: _footer, ...content } = report;
  const text = canonicalJson(content);
  let a = 2166136261;
  let b = 84696351;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 16777619);
    b = Math.imul(b ^ c, 16777619);
  }
  return (a >>> 0).toString(16).padStart(8, "0") + (b >>> 0).toString(16).padStart(8, "0");
}
function buildReportSnapshot(report, frozenAt = Date.now(), provenance = {}) {
  return {
    kind: REPORT_SNAPSHOT_KIND,
    version: REPORT_SNAPSHOT_VERSION,
    frozenAt,
    contentHash: reportContentHash(report),
    ...definedOnly(provenance),
    report
  };
}
function definedOnly(provenance) {
  const out = {};
  if (typeof provenance.caseRevision === "number") out.caseRevision = provenance.caseRevision;
  if (typeof provenance.generatedBy === "string" && provenance.generatedBy) out.generatedBy = provenance.generatedBy;
  if (typeof provenance.generatedAt === "number") out.generatedAt = provenance.generatedAt;
  return out;
}
var SECTION_KINDS = new Set(Object.values(ReportSectionKind));
var isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
function isSection(value) {
  return isRecord(value) && typeof value.id === "string" && typeof value.kind === "string" && SECTION_KINDS.has(value.kind);
}
function parseReportSnapshot(value) {
  if (!isRecord(value) || value.kind !== REPORT_SNAPSHOT_KIND || value.version !== REPORT_SNAPSHOT_VERSION) return null;
  const report = value.report;
  if (!isRecord(report) || typeof report.name !== "string" || !Array.isArray(report.sections)) return null;
  if (!report.sections.every(isSection)) return null;
  const definition = report;
  const contentHash = reportContentHash(definition);
  if (typeof value.contentHash === "string" && value.contentHash !== contentHash) return null;
  const frozenAt = typeof value.frozenAt === "number" && Number.isFinite(value.frozenAt) ? value.frozenAt : 0;
  return {
    kind: REPORT_SNAPSHOT_KIND,
    version: REPORT_SNAPSHOT_VERSION,
    frozenAt,
    contentHash,
    ...definedOnly({
      caseRevision: typeof value.caseRevision === "number" ? value.caseRevision : void 0,
      generatedBy: typeof value.generatedBy === "string" ? value.generatedBy : void 0,
      generatedAt: typeof value.generatedAt === "number" ? value.generatedAt : void 0
    }),
    report: definition
  };
}
var REPORT_ROW_KIND = "report";
function reportRowState(snapshot) {
  return {
    kind: REPORT_ROW_KIND,
    title: snapshot.report.name,
    ...snapshot.report.subject ? { subject: snapshot.report.subject } : {},
    frozenAt: snapshot.frozenAt,
    unsettled: Boolean(snapshot.report.status)
  };
}
function readReportRowState(extra) {
  if (!isRecord(extra) || extra.kind !== REPORT_ROW_KIND || typeof extra.title !== "string") return null;
  const subject = isRecord(extra.subject) && typeof extra.subject.type === "string" && typeof extra.subject.id === "string" ? { type: extra.subject.type, id: extra.subject.id } : void 0;
  return {
    kind: REPORT_ROW_KIND,
    title: extra.title,
    ...subject ? { subject } : {},
    frozenAt: typeof extra.frozenAt === "number" ? extra.frozenAt : 0,
    unsettled: extra.unsettled === true
  };
}

// src/reporting/addressNames.ts
function nameTable(section, nameOf) {
  if (section.kind !== "table") return section;
  const columns = section.columns.filter((c) => c.format === "address" && c.labelKey);
  if (!columns.length) return section;
  let changed = false;
  const rows = section.rows.map((row) => {
    let next = row;
    for (const col of columns) {
      const address = asString(row[col.key]);
      if (!address || asString(row[col.labelKey])) continue;
      const name = nameOf(address, col.chainKey ? asString(row[col.chainKey]) : void 0);
      if (!name) continue;
      if (next === row) next = { ...row };
      next[col.labelKey] = name;
      changed = true;
    }
    return next;
  });
  return changed ? { ...section, rows } : section;
}
function withAddressNames(report, nameOf) {
  const sections = report.sections.map((section) => nameTable(section, nameOf));
  return sections.every((s, i) => s === report.sections[i]) ? report : { ...report, sections };
}

export { AUTHORABLE_SECTION_KINDS, BAND_BUDGET, CENTER_WIDTH, ChartKind, DEFAULT_FLOW_WIDTH, MIN_BAND, MONO_ADVANCE, NODE_GAP, NODE_MIN_HEIGHT, NODE_WIDTH, PADDING_Y, REPORT_ROW_KIND, REPORT_SNAPSHOT_KIND, REPORT_SNAPSHOT_VERSION, ReportFormat, ReportSectionKind, ScheduleFrequency, WEEKDAY_LABELS, buildReportSnapshot, canonicalJson, defaultSchedule, describeRecurrence, fitLabel, layoutFlow, nextRunAt, parseReportSnapshot, parseTimeOfDay, readReportRowState, reportCellText, reportContentHash, reportRowState, withAddressNames };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map